// =============================================================================
// PRESENCE PROFILES — saved, versioned Get Found profiles (per user).
//
// Each generate-and-save is a NEW row, never an overwrite, so a user regenerates
// without fear of losing the previous version (the "no fear of loss" theme).
// Tagged with HOW it was made (target roles + platform + English variant), the
// full generated output, the skill set, and the platform-fit optimisation score
// captured at save time. Per-user (userId); deletes never happen here.
// =============================================================================

import { sql } from 'drizzle-orm';
import { index, jsonb, pgTable, uuid, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';
import { users } from './users';

/** How a public claim is grounded — mirrors the enabler-corpus-package.md vocabulary
 *  (docs/design/enabler-corpus-package.md): a RECEIPTED-FACT traces to a verifiable
 *  artifact (a repo timestamp, a file birth, a measured log); TESTIMONY is the user's
 *  own recorded words, carried honestly AS testimony, never upgraded to fact. */
export type PublicClaimType = 'receipted-fact' | 'testimony';

/**
 * One BOLD-CLAIMS line (W1-5(b)). The {claim, receipt, claimType} shape from the
 * enabler-corpus-package pattern: NOTHING here may exist without a receipt — a claim
 * with an empty/missing receipt is rejected before it ever reaches this shape
 * (PublicClaimsService.validateClaim), never merely hidden by the UI. `enabled`
 * defaults false everywhere it is constructed: every claim is DEFAULT OFF, surfaced
 * only after the user opts it in per-claim (never a bulk "publish all").
 */
export interface PublicClaim {
  /** Stable id for this claim (so a re-generate can preserve the user's opt-in choices). */
  id: string;
  /** The line as it would read on the profile/paste. */
  claim: string;
  /** What grounds it — a receipt string (source, timestamp, log line), never empty. */
  receipt: string;
  claimType: PublicClaimType;
  /** DEFAULT OFF. Only a claim the user has explicitly ticked may ever render. */
  enabled: boolean;
}

export const presenceProfiles = pgTable(
  'presence_profiles',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    // 'linkedin' | 'seek' | 'glassdoor'.
    platform: varchar('platform', { length: 40 }).notNull(),
    // Optional user-given label for the saved version.
    label: varchar('label', { length: 200 }),
    // The roles the user wanted to be found for (part of HOW it was generated).
    targetRoles: jsonb('target_roles').$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    // The English variant resolved at generation: en-AU | en-US | en-GB.
    englishVariant: varchar('english_variant', { length: 8 }),
    // The full generated PresenceProfile (headline, about, experience, skills, titles, paste).
    profile: jsonb('profile').notNull(),
    // The skill set, denormalised for query/filtering.
    skills: jsonb('skills').$type<string[]>().notNull().default(sql`'[]'::jsonb`),
    // The PlatformOptimisation (score + levers + field lengths) captured at save time.
    optimisation: jsonb('optimisation'),
    // Additive (generation loops, phase A 2026-07-02): the mint-context stamp —
    // { sourceEpoch, origin: 'user'|'offer', basedOnId?, scoreId? }. COLUMN ONLY tonight:
    // the presence writer lives in the LOCKED Get Found module, so its stamp waits on
    // Jason's per-change permission. NULL = "pre-loop, staleness unknown", never fabricated.
    provenance: jsonb('provenance'),
    // Additive (W1-5(b), launch-week §A, 2026-07-02): the BOLD-CLAIMS block —
    // PublicClaim[] (see PublicClaimsService), each { claim, receipt, claimType, enabled }.
    // ONLY receipt-backed lines may ever be appended (the {claim, receipt} shape from
    // docs/design/enabler-corpus-package.md); a claim with no receipt is rejected before
    // it reaches this column (PublicClaimsService.validateClaim), never merely hidden by
    // the UI. Each claim is DEFAULT OFF (enabled:false) — Jason opts each one in per-claim;
    // nothing here is ever surfaced on a paste/profile until he ticks it. COLUMN ONLY
    // tonight: the presence writer + the /generate response wire-in live in the LOCKED Get
    // Found module (presence.routes.ts), so they wait on Jason's per-change permission —
    // see docs/design/w1-5-locked-wirein.patch. NULL/empty = no claims staged yet, never
    // fabricated.
    publicClaims: jsonb('public_claims').$type<PublicClaim[]>(),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('presence_profiles_user_idx').on(t.userId),
    userPlatformIdx: index('presence_profiles_user_platform_idx').on(t.userId, t.platform),
  }),
);
